import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { AddressModel } from '@app/shared/models/address';
import { SupplierUtilityModel } from '@app/shared/models/supplier-utility-model';
import { AddressService } from '@app/shared/services/address.service';
import { UsAddressLookupService } from '@app/shared/services/us-address-lookup.service';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { supplierUtilityInformationSelector } from '@utility-information/state/selectors/utility-information.selectors';
import { first, map, switchMap } from 'rxjs/operators';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { UtilityInformation } from '@app/shared/models/utility-information';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { loggedInUtilityHubIdentitySelector } from '@app/state/app.selectors';
import { UtilityHubService } from '@app/shared/services/utilityhub.service';
import { SupplierUtilityInformation } from '@utility-information/state/actions/utility-information.actions';
import { CustomService } from '@app/custom/services/custom.service';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { CustomerType } from '@app/shared/models/customer-type';
import { ConfirmComponent } from '@app/shared/components/confirm/confirm.component';
import { cloneDeep, isEqual } from 'lodash';


@Component({
  selector: 'app-multiple-address-dialog',
  templateUrl: './multiple-address-dialog.component.html',
  styleUrls: ['./multiple-address-dialog.component.scss']
})


export class MultipleAddressDialogComponent implements OnInit {
  
  constructor(
    public multipleAddressDialogRef: MatDialogRef<MultipleAddressDialogComponent>,     
    @Inject(MAT_DIALOG_DATA) public data: any,
    private addressService: AddressService,
    private usLookup: UsAddressLookupService,
    private store: Store<AppState>,
    private utilityInformationService: UtilityInformationService,
    private utilityHubService : UtilityHubService,
    private CustomManualService: CustomService,
    private quoteRequestService: QuoteRequestService,
    private dialog: MatDialog
  ) {

  }

  utilityAddressData = [];
  initialUtilityAddressData = [];
  lookup$: Subscription;
  utilityAccountAddress$: Subscription;
  uhIdentity$: Subscription;
  quote$: Subscription;

  quoteRequestId: number;
  contractData: any;
  dataSource: MatTableDataSource<AddressModel>;
  commodity: CommodityType;
  customerType: CustomerType;
  quoteRequestType: QuoteRequestType;
  userIdentity: UtilityHubIdentity;
  externalId: string;
  isFormValidated: boolean = true;
  originalUtility: any;
  originalAddress: any;

  public columns = [
    { header: 'Account Number', field: 'utilityAccountNumber' },
    { header: 'Zip Code', field: 'zipCode' },
    { header: 'City', field: 'city' },
    { header: 'State', field: 'state' },
    { header: 'Address Line 1', field: 'addressLine1' },
    { header: 'Address Line 2', field: 'addressLine2' },
    { header: 'Validation', field: 'validation' },
  ];

  public displayedColumns: string[] = [
    'utilityAccountNumber',
    'zipCode',
    'validation',
    'city',
    'state',
    'addressLine1',
    'addressLine2',
  ];
  
  ngOnInit(): void {

    this.quoteRequestId = this.data.quoteRequestId;
    this.contractData = this.data.contractData;
    this.customerType = this.data.customerType;

    this.quote$ = forkJoin({
      state: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      quoteConfiguration: this.CustomManualService.getQuoteConfiguration(this.quoteRequestId),
    }).subscribe((res) => {      
      this.commodity = res.quoteConfiguration.utilityDetails.commodity;
      this.quoteRequestType = res.state.quoteRequestType;
      this.originalUtility = res.quoteConfiguration.utilityDetails;
      this.originalAddress = res.quoteConfiguration.customerDetails.location;
    });

    this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((uhUser) => { this.userIdentity = uhUser;});
    this.externalId = this.utilityHubService.getExternalId(this.userIdentity);
    this.utilityHubService.getSupplierUtilityInformation(this.externalId).subscribe(resp => {
      this.store.dispatch(new SupplierUtilityInformation(resp));
    });
    
    this.getUtilityAccountAddressData();

  }

  ngOnDestroy(): void {
    if (!!this.lookup$ && !this.lookup$.closed) this.lookup$.unsubscribe();
    if (!!this.utilityAccountAddress$ && !this.utilityAccountAddress$.closed) this.utilityAccountAddress$.unsubscribe();
    if (!!this.uhIdentity$ && !this.uhIdentity$.closed) this.uhIdentity$.unsubscribe();
    if (!!this.quote$ && !this.quote$.closed) this.quote$.unsubscribe();
  }

  onZipCodeChange(zipCode: string, address: AddressModel): void {
    if (this.validateZipCode(zipCode)) {
      address.isZipCodeLoading = true;
      this.lookup$ = this.usLookup.lookupStateFromZipCode(zipCode).subscribe(
        (results) => {
            if (results.success) {
              this.getUtilityInformation(results.stateCode, zipCode).pipe(
                switchMap(utilityInformation => {
                  const utilityShort = utilityInformation[0]?.utilityShort;
                  return this.getSupplierUtilityInformation().pipe(
                    map(supplierInformations => {
                      let isValidStateAndUtility = false;
                      if (supplierInformations.some(x => x.utility.shortName.toLocaleLowerCase() === utilityShort.toLocaleLowerCase())
                          && this.originalUtility.utilityShort.toLocaleLowerCase() === utilityShort.toLocaleLowerCase()
                          && this.originalAddress.state.toLocaleLowerCase() === results.stateCode.toLocaleLowerCase()) 
                      {
                        isValidStateAndUtility = true;
                      }
                      return isValidStateAndUtility;
                    })
                  );
                })
              ).subscribe(
                isValidStateAndUtility => {
                    if (isValidStateAndUtility) {
                      address.city = results.city;
                      address.state = results.stateCode;
                      address.addressLine1 = "";
                      address.isAddressLine1Valid = false;
                      address.addressLine2 = "";
                      this.setAddressValid(address);
                    } else {
                      //ZipCode not valid. Invalidate entry and show a warning message.
                      this.setAddressInvalid(address);
                      
                    }
                },
                error => {
                  console.error('An error occurred:', error);
                }
              );
            } else {
              this.setAddressInvalid(address);
            }
         
        },
        (err) => {
          this.setAddressInvalid(address);
        }
      );
    } else {
      this.setAddressInvalid(address);
    }    
  }

  validateAddressLine1(address: AddressModel): void {    
    address.isAddressLine1Valid = !!address.addressLine1.trim(); // Check if address line 1 is not empty
  }

  getUtilityInformation(stateCode: string, zipCode: string): Observable<UtilityInformation[]> {
    return this.utilityInformationService.getUtilityInformation(stateCode, this.quoteRequestType, this.commodity, zipCode);
  }

  getSupplierUtilityInformation(): Observable<SupplierUtilityModel[]> {
    return this.store.select(supplierUtilityInformationSelector).pipe(first());
  }

  async getUtilityAccountAddressData(): Promise<void> {
    
    this.utilityAccountAddress$ = this.addressService.getUtilityAccountAddressData(this.quoteRequestId, null).subscribe(
      (data) => {            
        this.utilityAddressData = data.addressList;
        if (!data.isMultipleAddressEnabled) {
          this.utilityAddressData.forEach(e => {
            e.country = "US",
            e.isZipCodeValid = true, // Making all zipCodes valid for initialization.
            e.isZipCodeLoading = false,
            e.addressLine1 = this.customerType == CustomerType.Commercial 
                                    ? this.contractData.company.companyAddress.addressLine1
                                    : this.contractData.customer.customerAddress.addressLine1,

            e.addressLine2 = this.customerType == CustomerType.Commercial 
                                    ? this.contractData.company.companyAddress.addressLine2
                                    : this.contractData.customer.customerAddress.addressLine2,

            e.isAddressLine1Valid = !!e.addressLine1.trim()
          });
        } else {
          this.utilityAddressData.forEach(e => {
            e.country = "US",
            e.isZipCodeValid = this.validateZipCode(e.zipCode),
            e.isZipCodeLoading = false,
            e.isAddressLine1Valid = !!e.addressLine1.trim()
          });
        } 

        this.initialUtilityAddressData = cloneDeep(this.utilityAddressData);
        this.dataSource = new MatTableDataSource(this.utilityAddressData);
      },
      (err) => {
        this.multipleAddressDialogRef.close(err as Error);
      }
    );
  }

  cancel(): void {
    this.multipleAddressDialogRef.close();
  }

  save(): void {
    const utilityAddressData = this.dataSource.data;
    this.addressService.saveUtilityAccountAddressData(this.quoteRequestId, utilityAddressData).subscribe(
      (response) => {
        this.multipleAddressDialogRef.close();
      },
      (err) => {
        alert("Something went wrong. Please try again.");
      }
    );
  }

  setAddressValid(address: AddressModel): void {
    address.isZipCodeValid = true;
    address.isZipCodeLoading = false;
    this.isFormValidated = true;
  }

  setAddressInvalid(address: AddressModel): void {
    address.isZipCodeValid = false;
    address.isZipCodeLoading = false;
    address.city = "";
    address.state = "";
    this.isFormValidated = false;
  }

  validateZipCode(zipCode: string): boolean {
    const zipCodePattern = /^\d{5}$/;
    return zipCodePattern.test(zipCode);
  }

  isFormValidForSave(): boolean {
    let result = true;
    if (this.dataSource?.data) {
      this.dataSource.data.forEach(element => {
        if (!element.isZipCodeValid || !element.isAddressLine1Valid) {
          result = false;
        }
      });
    }
    return result;
  }

  closeMultipleAddressDialogRef(): void {
    const utilityAddressDataHasChanged = !isEqual(this.initialUtilityAddressData, this.utilityAddressData);

    if (utilityAddressDataHasChanged) {
      const unsavedChangesDialogRef = this.dialog.open(ConfirmComponent, {
        width: '25%',
        data: {
          title: 'Warning Unsaved Changes',
          message: 'You have made changes that have not been saved. If you close now, these changes will be lost.<br><br> Do you want to proceed without saving?',
          primaryButtonText: 'Yes',
          negativeButtonText: 'No'
        },
        disableClose: true
      });
  
      unsavedChangesDialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.multipleAddressDialogRef.close();
        }
      });
    } else {
      this.multipleAddressDialogRef.close();
    }
  }
}
